import { regexExecAll } from '../utils/regexExecAll'
import { targetedAlphanumericFieldPattern, targetedKeyNotFieldPattern } from '../../regexp'
import type { AllDashboardCardFilters, SearchOperatorConfig } from '../../types'
import { RepTypesValuesEnum } from '../../Views/selectOptions'
import { appendAql, removeAql, replaceAql } from '../utils/aqlStringUtils'
import { mapAllFiltersToAQLFields } from '../utils/mapFiltersToAQLFields'
import { regexpCaptureGroupsToSearchOperatorConfig } from '../utils/searchOperatorConfig'
import { SearchOperators, type NotOperator } from '../../enums'

export const updateRepField = (
    prior: string,
    maybeType?: keyof typeof RepTypesValuesEnum | '',
    operator: NotOperator | '' = '',
    value = '',
): string => {
    const searchKey = 'rep'
    const aqlFields = mapAllFiltersToAQLFields[searchKey] || []
    const primaryAqlField = aqlFields[0]
    const type = maybeType || 'ALL'

    if (!primaryAqlField) {
        return prior
    }
    // Each member of the union has signatures, but none of those signatures are compatible with a string[]
    const preExistingAQLField = (aqlFields as unknown as string[]).reduce((acc, searchField) => {
        if (acc) {
            return acc
        }

        const pattern = targetedAlphanumericFieldPattern(searchField)
        const regex = new RegExp(pattern, 'gi')
        const match = regex.exec(prior)
        return match?.groups?.key || ''
    }, '')

    if (!value || !operator) {
        return preExistingAQLField ? removeAql(prior, preExistingAQLField) : prior
    }

    if (!preExistingAQLField) {
        const aqlFieldKey = type === 'ALL' ? 'rep' : `rep${type.toLowerCase()}`
        const aqlFieldKeyWithNot =
            operator === SearchOperators.NOT_EQUAL_TO ? (`${aqlFieldKey}Not` as const) : aqlFieldKey
        return appendAql(prior, aqlFieldKeyWithNot, value)
    }

    const lowerPreExistingAQLField = preExistingAQLField.toLowerCase()
    if (lowerPreExistingAQLField.includes('salesrep') && type === 'ALL') {
        if (lowerPreExistingAQLField.includes('not')) {
            const updatedSearchKey =
                operator === SearchOperators.NOT_EQUAL_TO
                    ? preExistingAQLField
                    : preExistingAQLField.replace(/not/i, '')

            return replaceAql(prior, preExistingAQLField, updatedSearchKey, value)
        }

        const updatedSearchKey =
            operator === SearchOperators.NOT_EQUAL_TO ? (`${preExistingAQLField}Not` as const) : preExistingAQLField

        return replaceAql(prior, preExistingAQLField, updatedSearchKey, value)
    }

    const aqlFieldKey = type === 'ALL' ? 'rep' : `rep${type.toLowerCase()}`
    const aqlFieldKeyWithNot = operator === SearchOperators.NOT_EQUAL_TO ? (`${aqlFieldKey}Not` as const) : aqlFieldKey

    const updatedSearchKey = operator === SearchOperators.NOT_EQUAL_TO ? aqlFieldKeyWithNot : aqlFieldKey

    return replaceAql(prior, preExistingAQLField, updatedSearchKey, value)
}

export const parseRepField = (aql: string): Partial<AllDashboardCardFilters> => {
    const searchKey = 'rep'
    const emptyConfig = { [searchKey]: '', [`${searchKey}Op`]: '', repType: 'ALL' as const }

    if (!aql) {
        return emptyConfig
    }

    const aqlFields = mapAllFiltersToAQLFields[searchKey] || []

    if (!aqlFields?.length) {
        return {}
    }

    // Each member of the union has signatures, but none of those signatures are compatible with a string[]
    const allMatches = (aqlFields as unknown as string[]).reduce<RegExpExecArray[]>((acc, field) => {
        if (acc.length) {
            return acc
        }

        const pattern = targetedKeyNotFieldPattern(field)
        const regex = new RegExp(pattern, 'gi')
        const maybeMatches = regexExecAll(aql, regex)

        return maybeMatches?.length ? maybeMatches : []
    }, [])

    if (!allMatches.length) {
        return emptyConfig
    }

    const firstConfig = allMatches.reduce<SearchOperatorConfig | null>((acc, match) => {
        if (acc) {
            return acc
        }

        const config = regexpCaptureGroupsToSearchOperatorConfig(match.groups)

        if (!config.operator || !config.lowValue) {
            return null
        }

        return config
    }, null)

    if (!firstConfig) {
        return emptyConfig
    }

    const { operator, lowValue, repType } = firstConfig
    return operator && lowValue
        ? {
              [searchKey]: lowValue,
              [`${searchKey}Op`]: operator,
              repType: operator ? repType || 'ALL' : undefined,
          }
        : emptyConfig
}
